/**
 * An overlay inside the chat window; the phone sheet is full screen. It
 * shows the 1024 px preview; the download button is the only way to the
 * original. Keys and counter follow WoundImageLightbox.
 */

import { useEffect, useRef } from 'react';
import { Button } from '@roger/ui-components';
import { ChevronLeft, ChevronRight, Download, X } from 'lucide-react';

import type { AttachmentUrl } from '../../hooks/chat/useAttachmentUrls';
import { sheetIsTornDown } from '../../hooks/chat/useMobileBackClose';
import { useStore } from '../../store';
import type { AttachmentOut } from '../../services/api';
import { extensionOf, formatFileSize } from '../../utils/attachments';

/** Module-level: one lightbox at a time, and a new mount cancels the pop. */
let pendingLightboxBack: ReturnType<typeof setTimeout> | undefined;

const isLightboxEntry = (state: unknown): boolean =>
  !!(state as { chatLightbox?: boolean } | null)?.chatLightbox;

interface AttachmentLightboxProps {
  attachments: AttachmentOut[];
  index: number;
  urls: Record<string, AttachmentUrl | null | undefined>;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  onDownload: (id: string) => void;
  /** False when the preview closes because another one opened. */
  returnsFocus?: () => boolean;
}

export function AttachmentLightbox({
  attachments,
  index,
  urls,
  onIndexChange,
  onClose,
  onDownload,
  returnsFocus,
}: AttachmentLightboxProps) {
  // The sheet (below 1024 px) is the layout that uses history entries.
  const isSheet = useStore(state => state.chatLayout === 'sheet');
  const current = attachments[index];
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const dialogRef = useRef<HTMLDivElement>(null);

  // Focus moves into the preview and returns to where it was on close, so the
  // covered composer cannot be typed into while the preview hides it.
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    dialogRef.current
      ?.querySelector<HTMLElement>('[aria-label="Close preview"]')
      ?.focus();
    return () => {
      if (returnsFocus?.() ?? true) previous?.focus?.();
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        // Keep Tab inside the preview.
        const focusable = Array.from(
          dialogRef.current?.querySelectorAll<HTMLElement>(
            'button:not([disabled])'
          ) ?? []
        );
        event.preventDefault();
        if (focusable.length === 0) return;
        const at = focusable.indexOf(document.activeElement as HTMLElement);
        const next = event.shiftKey
          ? at <= 0
            ? focusable.length - 1
            : at - 1
          : at === -1 || at === focusable.length - 1
            ? 0
            : at + 1;
        focusable[next].focus();
        return;
      }
      if (event.key === 'Escape') {
        // Window capture runs before any document listener of the chat
        // surface, and stopping here keeps Esc from closing the whole window.
        event.preventDefault();
        event.stopImmediatePropagation();
        onCloseRef.current();
      }
      if (event.key === 'ArrowRight' && index < attachments.length - 1)
        onIndexChange(index + 1);
      if (event.key === 'ArrowLeft' && index > 0) onIndexChange(index - 1);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [attachments.length, index, onIndexChange]);

  // One history entry for this layer on a phone (see the task note). The effect
  // must survive a StrictMode mount-cleanup-mount: the cleanup only schedules
  // its pop, a re-mount cancels it and keeps the entry already on top, so no
  // traversal lands mid-mount and no entry leaks.
  useEffect(() => {
    if (!isSheet) return;
    clearTimeout(pendingLightboxBack);
    if (!isLightboxEntry(window.history.state))
      window.history.pushState({ chatLightbox: true }, '');
    let popped = false;
    const onPopState = (event: PopStateEvent) => {
      if (isLightboxEntry(event.state)) return;
      popped = true;
      onCloseRef.current();
    };
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
      if (popped) return;
      // Closed by a control: pop our own entry, and only if it is still on top.
      pendingLightboxBack = setTimeout(() => {
        // The sheet went away too: its teardown removes this entry with its own.
        if (sheetIsTornDown()) return;
        if (isLightboxEntry(window.history.state)) window.history.back();
      });
    };
  }, [isSheet]);

  if (!current) return null;

  return (
    <div
      ref={dialogRef}
      role='dialog'
      aria-modal='true'
      aria-label={current.file_name}
      className='absolute inset-0 z-50 flex flex-col bg-black/90'
    >
      <div className='flex items-center gap-2 p-2 text-white'>
        <p className='min-w-0 flex-1 truncate text-sm'>{current.file_name}</p>
        <span className='whitespace-nowrap text-xs'>
          {`${extensionOf(current.file_name).toUpperCase()} · ${formatFileSize(current.size_bytes)}`}
          {current.page_count ? ` · ${current.page_count} pages` : ''}
        </span>
        <Button
          size='icon'
          variant='ghost'
          aria-label='Download'
          onClick={() => onDownload(current.id)}
        >
          <Download className='h-4 w-4' />
        </Button>
        <Button
          size='icon'
          variant='ghost'
          aria-label='Close preview'
          onClick={onClose}
        >
          <X className='h-4 w-4' />
        </Button>
      </div>

      <div className='flex min-h-0 flex-1 items-center justify-center gap-2 px-2'>
        <Button
          size='icon'
          variant='ghost'
          aria-label='Previous'
          disabled={index === 0}
          onClick={() => onIndexChange(index - 1)}
        >
          <ChevronLeft className='h-5 w-5' />
        </Button>
        <img
          src={urls[current.id]?.previewUrl}
          alt={current.file_name}
          className='max-h-full min-w-0 max-w-full object-contain'
        />
        <Button
          size='icon'
          variant='ghost'
          aria-label='Next'
          disabled={index === attachments.length - 1}
          onClick={() => onIndexChange(index + 1)}
        >
          <ChevronRight className='h-5 w-5' />
        </Button>
      </div>

      <p className='pb-2 text-center text-xs text-white'>
        {index + 1} of {attachments.length}
      </p>
    </div>
  );
}
